import type {
  GreatKingdomAttackWarning,
  GreatKingdomBoard,
  GreatKingdomCapturedGroup,
  GreatKingdomCell,
  GreatKingdomPlayerId,
  GreatKingdomPosition,
  GreatKingdomState,
  GreatKingdomTerritoryRegion,
  GreatKingdomTerritorySummary,
  GreatKingdomViewerState,
} from "../types";
import { GREAT_KINGDOM_BOARD_SIZE, GREAT_KINGDOM_PLAYERS } from "../types";

const ORTHOGONAL_DIRECTIONS = [
  { row: -1, col: 0 },
  { row: 0, col: 1 },
  { row: 1, col: 0 },
  { row: 0, col: -1 },
] as const;

interface CastleGroup {
  readonly owner: GreatKingdomPlayerId;
  readonly cells: readonly GreatKingdomPosition[];
  readonly liberties: readonly GreatKingdomPosition[];
}

export interface TerritoryAnalysis {
  readonly summary: GreatKingdomTerritorySummary;
  readonly regions: readonly GreatKingdomTerritoryRegion[];
  readonly cellOwners: Readonly<Record<string, GreatKingdomPlayerId>>;
}

export function positionKey(position: GreatKingdomPosition): string {
  return `${position.row},${position.col}`;
}

export function isInBounds(row: number, col: number): boolean {
  return (
    Number.isInteger(row) &&
    Number.isInteger(col) &&
    row >= 0 &&
    row < GREAT_KINGDOM_BOARD_SIZE &&
    col >= 0 &&
    col < GREAT_KINGDOM_BOARD_SIZE
  );
}

export function getCell(
  board: GreatKingdomBoard,
  row: number,
  col: number,
): GreatKingdomCell | null {
  if (!isInBounds(row, col)) return null;
  return board[row]?.[col] ?? null;
}

export function setCell(
  board: GreatKingdomBoard,
  row: number,
  col: number,
  value: GreatKingdomCell,
): GreatKingdomBoard {
  if (!isInBounds(row, col)) {
    throw new Error("Cannot set a cell outside the Great Kingdom board");
  }

  return board.map((line, lineIndex) =>
    lineIndex === row
      ? line.map((cell, cellIndex) => (cellIndex === col ? value : cell))
      : line,
  );
}

export function clearCells(
  board: GreatKingdomBoard,
  cells: readonly GreatKingdomPosition[],
): GreatKingdomBoard {
  const cleared = new Set(cells.map(positionKey));
  return board.map((line, row) =>
    line.map((cell, col) =>
      cleared.has(positionKey({ row, col })) ? "empty" : cell,
    ),
  );
}

export function getOpponentPlayerId(
  playerId: GreatKingdomPlayerId,
): GreatKingdomPlayerId {
  return playerId === "p1" ? "p2" : "p1";
}

export function isPlayerId(value: unknown): value is GreatKingdomPlayerId {
  return (
    typeof value === "string" &&
    GREAT_KINGDOM_PLAYERS.includes(value as GreatKingdomPlayerId)
  );
}

export function listNeighbors(
  position: GreatKingdomPosition,
): readonly GreatKingdomPosition[] {
  return ORTHOGONAL_DIRECTIONS.map((direction) => ({
    row: position.row + direction.row,
    col: position.col + direction.col,
  })).filter((neighbor) => isInBounds(neighbor.row, neighbor.col));
}

function hasAllBoardEdges(edges: ReadonlySet<string>): boolean {
  return (
    edges.has("top") &&
    edges.has("right") &&
    edges.has("bottom") &&
    edges.has("left")
  );
}

function edgeNamesFor(position: GreatKingdomPosition): readonly string[] {
  const edges: string[] = [];
  if (position.row === 0) edges.push("top");
  if (position.col === GREAT_KINGDOM_BOARD_SIZE - 1) edges.push("right");
  if (position.row === GREAT_KINGDOM_BOARD_SIZE - 1) edges.push("bottom");
  if (position.col === 0) edges.push("left");
  return edges;
}

function floodEmptyRegion(
  board: GreatKingdomBoard,
  start: GreatKingdomPosition,
  visited: Set<string>,
): GreatKingdomTerritoryRegion {
  const cells: GreatKingdomPosition[] = [];
  const queue: GreatKingdomPosition[] = [start];
  const boundaryPlayers = new Set<GreatKingdomPlayerId>();
  const touchedEdges = new Set<string>();
  visited.add(positionKey(start));

  for (let index = 0; index < queue.length; index += 1) {
    const current = queue[index];
    if (!current) continue;

    cells.push(current);
    for (const edgeName of edgeNamesFor(current)) {
      touchedEdges.add(edgeName);
    }

    for (const neighbor of listNeighbors(current)) {
      const neighborCell = getCell(board, neighbor.row, neighbor.col);
      if (neighborCell === "empty") {
        const key = positionKey(neighbor);
        if (!visited.has(key)) {
          visited.add(key);
          queue.push(neighbor);
        }
      } else if (neighborCell === "p1" || neighborCell === "p2") {
        boundaryPlayers.add(neighborCell);
      }
    }
  }

  const owner =
    boundaryPlayers.size === 1 ? ([...boundaryPlayers][0] ?? null) : null;

  return {
    owner,
    cells,
    touchesAllEdges: hasAllBoardEdges(touchedEdges),
  };
}

export function calculateTerritory(
  board: GreatKingdomBoard,
): TerritoryAnalysis {
  const visited = new Set<string>();
  const regions: GreatKingdomTerritoryRegion[] = [];
  const cellOwnerEntries: [string, GreatKingdomPlayerId][] = [];
  const summary: { p1: number; p2: number } = { p1: 0, p2: 0 };

  for (let row = 0; row < GREAT_KINGDOM_BOARD_SIZE; row += 1) {
    for (let col = 0; col < GREAT_KINGDOM_BOARD_SIZE; col += 1) {
      const key = positionKey({ row, col });
      if (visited.has(key) || getCell(board, row, col) !== "empty") continue;

      const region = floodEmptyRegion(board, { row, col }, visited);
      regions.push(region);

      if (!region.owner || region.touchesAllEdges) continue;

      summary[region.owner] += region.cells.length;
      for (const cell of region.cells) {
        cellOwnerEntries.push([positionKey(cell), region.owner]);
      }
    }
  }

  return {
    summary,
    regions,
    cellOwners: Object.fromEntries(cellOwnerEntries),
  };
}

export function calculateScoreEstimate(
  board: GreatKingdomBoard,
): GreatKingdomTerritorySummary {
  return calculateTerritory(board).summary;
}

export function isCellInOpponentTerritory(
  board: GreatKingdomBoard,
  row: number,
  col: number,
  playerId: GreatKingdomPlayerId,
): boolean {
  const analysis = calculateTerritory(board);
  return (
    analysis.cellOwners[positionKey({ row, col })] ===
    getOpponentPlayerId(playerId)
  );
}

function collectCastleGroup(
  board: GreatKingdomBoard,
  start: GreatKingdomPosition,
  owner: GreatKingdomPlayerId,
  visited: Set<string>,
): CastleGroup {
  const cells: GreatKingdomPosition[] = [];
  const libertiesByKey = new Map<string, GreatKingdomPosition>();
  const queue: GreatKingdomPosition[] = [start];
  visited.add(positionKey(start));

  for (let index = 0; index < queue.length; index += 1) {
    const current = queue[index];
    if (!current) continue;

    cells.push(current);
    for (const neighbor of listNeighbors(current)) {
      const neighborCell = getCell(board, neighbor.row, neighbor.col);
      if (neighborCell === owner) {
        const key = positionKey(neighbor);
        if (!visited.has(key)) {
          visited.add(key);
          queue.push(neighbor);
        }
      } else if (neighborCell === "empty") {
        libertiesByKey.set(positionKey(neighbor), neighbor);
      }
    }
  }

  return {
    owner,
    cells,
    liberties: [...libertiesByKey.values()],
  };
}

export function findCastleGroups(
  board: GreatKingdomBoard,
  owner: GreatKingdomPlayerId,
): readonly CastleGroup[] {
  const visited = new Set<string>();
  const groups: CastleGroup[] = [];

  for (let row = 0; row < GREAT_KINGDOM_BOARD_SIZE; row += 1) {
    for (let col = 0; col < GREAT_KINGDOM_BOARD_SIZE; col += 1) {
      const key = positionKey({ row, col });
      if (visited.has(key) || getCell(board, row, col) !== owner) continue;
      groups.push(collectCastleGroup(board, { row, col }, owner, visited));
    }
  }

  return groups;
}

export function findCapturedGroupsAfterMove(
  board: GreatKingdomBoard,
  actorPlayerId: GreatKingdomPlayerId,
): readonly GreatKingdomCapturedGroup[] {
  const opponentId = getOpponentPlayerId(actorPlayerId);
  return findCastleGroups(board, opponentId)
    .filter((group) => group.liberties.length === 0)
    .map((group) => ({
      owner: group.owner,
      cells: group.cells,
    }));
}

export function getAttackWarnings(
  state: GreatKingdomState,
  playerId: GreatKingdomPlayerId,
): readonly GreatKingdomAttackWarning[] {
  const opponentId = getOpponentPlayerId(playerId);
  return findCastleGroups(state.board, opponentId)
    .filter((group) => group.liberties.length === 1)
    .flatMap((group) => {
      const liberty = group.liberties[0];
      return liberty
        ? [
            {
              owner: group.owner,
              group: group.cells,
              liberty,
            },
          ]
        : [];
    });
}

export function applyScoreEstimate(
  state: GreatKingdomState,
  territory: GreatKingdomTerritorySummary,
): GreatKingdomState {
  return {
    ...state,
    territory,
    players: state.players.map((player) => ({
      ...player,
      scoreEstimate: territory[player.playerId],
    })),
  };
}

export function filterStateForViewer(
  state: GreatKingdomState,
  _viewerPlayerId: string | null,
): GreatKingdomViewerState {
  return state;
}

export function buildViewerStates(
  state: GreatKingdomState,
  observerUserIds: readonly string[] = [],
): Readonly<Record<string, GreatKingdomViewerState>> {
  const playerEntries = state.players.map(
    (player) =>
      [player.playerId, filterStateForViewer(state, player.playerId)] as const,
  );
  const observerEntries = [...new Set(observerUserIds)].map(
    (observerUserId) =>
      [observerUserId, filterStateForViewer(state, null)] as const,
  );

  return Object.fromEntries([...playerEntries, ...observerEntries]);
}
